/**
 * Main Zoom in-meeting app.
 *
 * Flow:
 *   1. Configure the Zoom SDK
 *   2. Get meeting + user context
 *   3. Run PKCE auth flow to exchange Zoom code for a session token
 *   4. Connect to Socket.IO with the session token
 *   5. Render facilitator or participant view based on role
 *
 * Breakout room handling:
 *   - When joining a breakout room, show a pause overlay
 *   - When leaving, refresh the active node
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useZoomContext } from './hooks/useZoomContext';
import { useSessionSocket, type SocketEvents } from './hooks/useSessionSocket';
import { startZoomAuth, type ZoomAuthResult } from './lib/zoomAuth';
import { SessionPicker } from './components/SessionPicker';
import { UnlinkedScreen } from './components/UnlinkedScreen';
import {
  getSessionNodes,
  registerParticipant,
  getSessionId,
  getActiveNodeIndex,
  type SessionNode,
} from './lib/api';
import { expandSidebar } from './lib/zoomActions';
import { ZoomFacilitatorView } from './components/ZoomFacilitatorView';
import { ZoomParticipantView } from './components/ZoomParticipantView';
import { ZoomCollaborateDisplay } from './components/ZoomCollaborateDisplay';
import { WaitingRoomScreen } from './components/WaitingRoomScreen';

/* ------------------------------------------------------------------ */
/*  App state                                                         */
/* ------------------------------------------------------------------ */

// 'pick'     — host with a connected account, no session linked yet: choose one
// 'unlinked' — nothing linked and the caller can't link from here (participant
//              waiting for the host, or a host who hasn't connected Zoom yet)
type AppPhase =
  | 'init'
  | 'auth'
  | 'loading'
  | 'pick'
  | 'unlinked'
  | 'waiting'
  | 'active'
  | 'error';

export default function App() {
  const zoom = useZoomContext();

  const [phase, setPhase] = useState<AppPhase>('init');
  const [errorMsg, setErrorMsg] = useState('');
  const [authResult, setAuthResult] = useState<ZoomAuthResult | null>(null);
  const [activeNodeIndex, setActiveNodeIndex] = useState(-1);
  const [nodes, setNodes] = useState<SessionNode[]>([]);
  const [responseCounts, setResponseCounts] = useState<Record<string, number>>({});
  const [hostToken, setHostToken] = useState<string | null>(null);
  const [unlinkedAsHost, setUnlinkedAsHost] = useState(false);
  // Bumped by "Check again" on the unlinked screen to re-run the auth flow.
  const [authAttempt, setAuthAttempt] = useState(0);
  const authStartedRef = useRef(false);

  const sessionId = authResult?.sessionId ?? getSessionId();
  const isFacilitator = authResult?.role === 'facilitator' || zoom.isHost;

  /* ---------------------------------------------------------------- */
  /*  Socket event handlers                                           */
  /* ---------------------------------------------------------------- */

  const socketEvents: SocketEvents = {
    onNodeChanged: (data) => {
      setActiveNodeIndex(data.activeNodeIndex);
      // Reload nodes to get fresh data
      if (sessionId) {
        getSessionNodes(sessionId).then(setNodes).catch(console.error);
      }
      // Participants: pop the sidebar open so the new activity is seen
      // (best-effort — capability may be disabled).
      if (!isFacilitator) {
        expandSidebar().catch(() => {});
      }
    },
    onSessionIdle: () => {
      setPhase('waiting');
    },
    onNewResponse: (data) => {
      setResponseCounts((prev) => ({
        ...prev,
        [data.nodeId]: data.responseCount,
      }));
    },
    onPollResultsUpdated: () => {
      // Refresh nodes to get updated results
      if (sessionId) {
        getSessionNodes(sessionId).then(setNodes).catch(console.error);
      }
    },
    onNpsResultsUpdated: () => {
      if (sessionId) {
        getSessionNodes(sessionId).then(setNodes).catch(console.error);
      }
    },
    onQuizResultsUpdated: () => {
      if (sessionId) {
        getSessionNodes(sessionId).then(setNodes).catch(console.error);
      }
    },
  };

  const { isPolling } = useSessionSocket(sessionId, socketEvents);

  /* ---------------------------------------------------------------- */
  /*  Auth flow                                                       */
  /* ---------------------------------------------------------------- */

  /**
   * Enter a linked session: load its nodes, register on the roster if we
   * are a participant, and pick the starting phase from the server's
   * CURRENT state. Shared by the normal exchange and the host picker.
   */
  const enterSession = useCallback(
    async (result: ZoomAuthResult) => {
      setAuthResult(result);

      // Load initial nodes
      const nodeData = await getSessionNodes(result.sessionId);
      setNodes(nodeData);

      // Register on the roster if not facilitator. Best-effort — the
      // participant view mints its own participant token for submissions,
      // so a roster-registration failure must not block the session.
      const isHost = result.role === 'facilitator' || zoom.isHost;
      if (!isHost && zoom.user) {
        registerParticipant(result.sessionId, {
          displayName: zoom.user.screenName,
          zoomUserId: zoom.user.participantId,
        }).catch((err) => console.warn('Roster registration failed:', err));
      }

      // Determine initial phase + active node from the server's CURRENT
      // state — don't assume node 0. The session may already be mid-activity
      // (resuming a session, or a second surface/the collaborate window
      // joining), so each instance must reflect the server's active node or
      // the sidebar and the shared display drift apart.
      if (nodeData.length === 0) {
        setPhase('waiting');
      } else {
        let startIndex = 0;
        try {
          const active = await getActiveNodeIndex(result.sessionId);
          if (typeof active.activeNodeIndex === 'number' && active.activeNodeIndex >= 0) {
            startIndex = active.activeNodeIndex;
          }
        } catch {
          /* fall back to the first node */
        }
        setActiveNodeIndex(startIndex);
        setPhase('active');
      }
    },
    [zoom.isHost, zoom.user],
  );

  useEffect(() => {
    if (zoom.loading || !zoom.configured || authStartedRef.current) return;
    if (!zoom.meeting?.meetingID) return;

    authStartedRef.current = true;
    setPhase('auth');

    (async () => {
      try {
        const outcome = await startZoomAuth(
          zoom.meeting!.meetingID,
          zoom.user?.role ?? 'attendee',
        );

        if (outcome.status === 'unlinked') {
          // No session linked to this meeting yet. A verified host with a
          // connected account picks one here; anyone else waits (or, for a
          // host who hasn't connected Zoom to their account, is told how).
          if (outcome.canLink && outcome.hostToken) {
            setHostToken(outcome.hostToken);
            setPhase('pick');
          } else {
            setUnlinkedAsHost(outcome.isHost || zoom.isHost);
            setPhase('unlinked');
          }
          return;
        }

        await enterSession(outcome);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Authentication failed';
        console.error('Auth error:', message);
        setErrorMsg(message);
        setPhase('error');
      }
    })();
  }, [zoom.loading, zoom.configured, zoom.meeting, zoom.isHost, zoom.user, enterSession, authAttempt]);

  /** "Check again" on the unlinked screen: run the Zoom authorize flow afresh. */
  const retryAuth = useCallback(() => {
    authStartedRef.current = false;
    setHostToken(null);
    setPhase('init');
    setAuthAttempt((n) => n + 1);
  }, []);

  /** The host picked a session: the server already issued the session token. */
  const handleSessionLinked = useCallback(
    (result: ZoomAuthResult) => {
      setHostToken(null);
      setPhase('loading');
      enterSession(result).catch((err) => {
        const message = err instanceof Error ? err.message : 'Could not open the session';
        console.error('Enter session error:', message);
        setErrorMsg(message);
        setPhase('error');
      });
    },
    [enterSession],
  );

  /* ---------------------------------------------------------------- */
  /*  Breakout room handling                                          */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    if (!zoom.inBreakoutRoom || !sessionId) return;

    // When leaving the breakout room, refresh the active node
    return () => {
      getSessionNodes(sessionId).then((data) => {
        setNodes(data);
      }).catch(console.error);
    };
  }, [zoom.inBreakoutRoom, sessionId]);

  /* ---------------------------------------------------------------- */
  /*  Callbacks                                                       */
  /* ---------------------------------------------------------------- */

  const handleNodeChange = useCallback((index: number) => {
    setActiveNodeIndex(index);
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Render                                                          */
  /* ---------------------------------------------------------------- */

  // Loading / init
  if (phase === 'init' || zoom.loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white px-4">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent mb-4" />
        <p className="text-sm text-gray-500">Connecting to Zoom...</p>
      </div>
    );
  }

  // Zoom SDK error
  if (zoom.error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white px-4">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <svg
            className="w-6 h-6 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-900 mb-1">
          Zoom SDK Error
        </p>
        <p className="text-xs text-gray-500 text-center">{zoom.error}</p>
      </div>
    );
  }

  // Auth in progress
  if (phase === 'auth') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white px-4">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent mb-4" />
        <p className="text-sm text-gray-500">Authenticating...</p>
      </div>
    );
  }

  // Loading session
  if (phase === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white px-4">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent mb-4" />
        <p className="text-sm text-gray-500">Loading session...</p>
      </div>
    );
  }

  // No session linked yet: the host chooses one from their account.
  if (phase === 'pick' && hostToken) {
    return (
      <SessionPicker
        hostToken={hostToken}
        meetingTopic={zoom.meeting?.meetingTopic}
        onLinked={handleSessionLinked}
        onExpired={retryAuth}
      />
    );
  }

  // No session linked yet and the caller cannot link one from here.
  if (phase === 'unlinked') {
    return (
      <UnlinkedScreen
        isHost={unlinkedAsHost}
        meetingId={zoom.meeting?.meetingID}
        onRetry={retryAuth}
      />
    );
  }

  // Error
  if (phase === 'error') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white px-4">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <svg
            className="w-6 h-6 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-900 mb-1">
          Something went wrong
        </p>
        <p className="text-xs text-gray-500 text-center mb-4">{errorMsg}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50
            rounded-lg hover:bg-primary-100 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Collaborate context: the host shared the app to the main meeting window.
  // Everyone (host included) sees the live display page, not the control
  // panel — the display handles its own idle/active rendering.
  if (
    zoom.runningContext === 'inCollaborate' &&
    sessionId &&
    (phase === 'waiting' || phase === 'active')
  ) {
    return (
      <ZoomCollaborateDisplay
        sessionId={sessionId}
        isFacilitator={isFacilitator}
        activeNode={
          activeNodeIndex >= 0 && activeNodeIndex < nodes.length
            ? nodes[activeNodeIndex]
            : null
        }
        activeNodeIndex={activeNodeIndex}
        nodeCount={nodes.length}
        onNodeChange={handleNodeChange}
      />
    );
  }

  // Waiting room
  if (phase === 'waiting' && sessionId) {
    return (
      <WaitingRoomScreen
        sessionId={sessionId}
        sessionTitle={zoom.meeting?.meetingTopic}
      />
    );
  }

  // Breakout room overlay
  if (zoom.inBreakoutRoom) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white px-4">
        <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
          <svg
            className="w-6 h-6 text-yellow-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-900 mb-1">
          Breakout Room
        </p>
        <p className="text-xs text-gray-500 text-center">
          Session activities are paused while you're in a breakout room.
          They'll resume when you return.
        </p>
      </div>
    );
  }

  // Active session
  if (phase === 'active' && sessionId) {
    if (isFacilitator) {
      return (
        <ZoomFacilitatorView
          sessionId={sessionId}
          activeNodeIndex={activeNodeIndex}
          isPolling={isPolling}
          onNodeChange={handleNodeChange}
          responseCounts={responseCounts}
        />
      );
    }

    const activeNode =
      activeNodeIndex >= 0 && activeNodeIndex < nodes.length
        ? nodes[activeNodeIndex]
        : null;

    return (
      <ZoomParticipantView
        sessionId={sessionId}
        node={activeNode}
        isPolling={isPolling}
        displayName={zoom.user?.screenName || 'Zoom participant'}
      />
    );
  }

  // Fallback
  return (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <p className="text-sm text-gray-400">Initializing...</p>
    </div>
  );
}
